import { CartLineNode } from '../shopify';
import {
  isSubscriptionAddon,
  createAttributesMap,
  createGetPropertyFunction,
} from './helpers';
import { removeFromCart } from '../cart.browser';
import { extractIdFromShopifyStr } from '@/utils/helpers';

/**
 * Checks if there are multiple subscription-1 items in the cart
 * @param edges - The cart line edges from the cart response
 * @returns true if there are multiple subscription-1 items, false otherwise
 */
export function hasMultipleSubscriptionItems(
  edges: { node: CartLineNode }[],
): boolean {
  const subscriptionItemsCount = edges.filter(
    ({ node }) => node.merchandise.product?.handle === 'subscription-1',
  ).length;

  return subscriptionItemsCount > 1;
}

/**
 * Gets all line IDs that should be removed due to incompatible subscription items
 * @param edges - The cart line edges from the cart response
 * @returns Array of line IDs to remove (subscriptions + their addons)
 */
export function getSubscriptionItemsToRemove(
  edges: { node: CartLineNode }[],
): string[] {
  // If no multiple subscriptions, return empty array
  if (!hasMultipleSubscriptionItems(edges)) {
    return [];
  }

  // Find all subscription-1 items
  const subscriptionItems = edges.filter(
    ({ node }) => node.merchandise.product?.handle === 'subscription-1',
  );

  // Keep only the last subscription-1 item (keep the last one added)
  const subscriptionItemsToRemove = subscriptionItems.slice(0, -1);

  // Find all addons for each subscription item we're removing
  const allItemsToRemove = new Set<string>();
  subscriptionItemsToRemove.forEach(({ node: subscriptionNode }) => {
    const itemsToRemoveForThisSubscription = findSubscriptionItemsToRemove(
      subscriptionNode.id,
      edges,
    );
    itemsToRemoveForThisSubscription.forEach((itemId) =>
      allItemsToRemove.add(itemId),
    );
  });

  // Collect all IDs to remove (subscriptions + their addons)
  return Array.from(allItemsToRemove);
}

/**
 * Checks if a product is a Reading Skill Set Complete Set
 * @param handle - The product handle to check
 * @returns true if it's the complete reading skill set, false otherwise
 */
export function isReadingSkillSetComplete(handle: string): boolean {
  // Note: Handle can be 'the-complete-reading-skill-set' in Australia
  return (
    handle === 'reading-skill-set' ||
    handle === 'the-complete-reading-skill-set'
  );
}

/**
 * Checks if a product is an individual Reading Skill Set part
 * @param handle - The product handle to check
 * @returns true if it's an individual reading skill set part, false otherwise
 */
export function isReadingSkillSetPart(handle: string): boolean {
  return handle?.startsWith?.('reading-skill-set-part-') ?? false;
}

/**
 * Finds Reading Skill Set items that are incompatible when Complete Set is present
 * @param edges - The cart line edges from the cart response
 * @returns Array of line IDs to remove (individual parts when complete set exists)
 */
export function getReadingSkillSetItemsToRemove(
  edges: { node: CartLineNode }[],
): string[] {
  // Check if we have the complete set
  const hasCompleteSet = edges.some(
    ({ node }) =>
      node.merchandise.product?.handle &&
      isReadingSkillSetComplete(node.merchandise.product.handle),
  );

  // If no complete set, no conflicts to resolve
  if (!hasCompleteSet) {
    return [];
  }

  // Find all individual parts to remove
  const partsToRemove = edges.filter(
    ({ node }) =>
      node.merchandise.product?.handle &&
      isReadingSkillSetPart(node.merchandise.product.handle),
  );

  return partsToRemove.map(({ node }) => node.id);
}

/**
 * Finds all addons linked to a specific subscription
 * @param subscriptionLineId - The line ID of the subscription-1 item
 * @param edges - All cart line edges
 * @returns Array of addon line IDs (without the subscription itself)
 */
export function findSubscriptionAddons(
  subscriptionLineId: string,
  edges: { node: CartLineNode }[],
): string[] {
  // Find the subscription node
  const subscriptionNode = edges.find(
    ({ node }) => node.id === subscriptionLineId,
  )?.node;

  if (
    !subscriptionNode ||
    subscriptionNode.merchandise.product?.handle !== 'subscription-1'
  ) {
    return []; // Not a subscription-1 item, no addons
  }

  const addonIds: string[] = [];

  // Find all addons linked to this subscription using the shared helper
  edges.forEach(({ node: potentialAddon }) => {
    if (isSubscriptionAddon(potentialAddon, subscriptionNode)) {
      addonIds.push(potentialAddon.id);
    }
  });

  return addonIds;
}

/**
 * Gets duplicate subscription addons to remove (keeps first addon, removes rest)
 * @param edges - The cart line edges from the cart response
 * @returns Array of line IDs of duplicate subscription addons to remove
 */
export function getDuplicateSubscriptionAddonsToRemove(
  edges: { node: CartLineNode }[],
): string[] {
  const duplicateAddonsToRemove: string[] = [];

  // Find all subscription-1 items
  const subscriptionItems = edges.filter(
    ({ node }) => node.merchandise.product?.handle === 'subscription-1',
  );

  // For each subscription, check if it has multiple addons
  subscriptionItems.forEach(({ node: subscriptionNode }) => {
    const addonIds = findSubscriptionAddons(subscriptionNode.id, edges);

    // If there are more than one addon, keep the first and mark the rest for removal
    if (addonIds.length > 1) {
      // Keep the first addon, remove the rest
      const addonsToRemove = addonIds.slice(1);
      duplicateAddonsToRemove.push(...addonsToRemove);
    }
  });

  return duplicateAddonsToRemove;
}

/**
 * Extracts the kit name from a starting_kit_anchor value
 * @param startingKitAnchor - e.g., "kit-the-observer"
 * @returns Kit name (e.g., "observer") or null if not found
 */
export function extractKitNameFromAnchor(
  startingKitAnchor: string,
): string | null {
  // "kit-the-observer" -> "observer"
  const match = startingKitAnchor?.match(/^kit-the-(.+)$/);
  return match ? match[1] : null;
}

/**
 * Extracts the kit name from a book bundle handle
 * @param handle - e.g., "book-bundle-the-babbler"
 * @returns Kit name (e.g., "babbler") or null if not found
 */
export function extractKitNameFromBookHandle(handle: string): string | null {
  // "book-bundle-the-babbler" -> "babbler"
  const match = handle?.match(/^book-bundle-the-(.+)$/);
  return match ? match[1] : null;
}

/**
 * Extracts variant IDs from the add_ons array in a subscription payload
 * @param subscriptionsPayload - The subscriptionsPayload string from subscription attributes
 * @returns Array of variant IDs from the add_ons array
 */
export function extractVariantIdsFromAddOns(
  subscriptionsPayload: string,
): string[] {
  const variantIds: string[] = [];

  // Use regex to find all variantId values in the add_ons array
  // Pattern matches: "variantId" => "40011055759475"
  const variantIdRegex = /"variantId"\s*=>\s*"(\d+)"/g;
  let match;

  while ((match = variantIdRegex.exec(subscriptionsPayload)) !== null) {
    variantIds.push(match[1]);
  }

  return variantIds;
}

/**
 * Gets mismatched book addons that don't match their subscription's allowed variant IDs
 * @param edges - The cart line edges from the cart response
 * @returns Array of line IDs of mismatched book addons to remove
 * @performance O(n × m), where n is the number of cart lines and m is the number of subscriptions, using pre-computed attribute maps
 */
export function getMismatchedBookAddonsToRemove(
  edges: { node: CartLineNode }[],
): string[] {
  // Early return if no edges
  if (edges.length === 0) {
    return [];
  }

  // Step 1: Pre-compute attribute maps for all nodes (O(n))
  const nodeAttributesMap = new Map<string, Map<string, string>>();
  const nodeGetPropertyMap = new Map<
    string,
    (key: string) => string | undefined
  >();

  edges.forEach(({ node }) => {
    const attributesMap = createAttributesMap(node.attributes || []);
    nodeAttributesMap.set(node.id, attributesMap);
    nodeGetPropertyMap.set(node.id, createGetPropertyFunction(attributesMap));
  });

  // Step 2: Build subscription allowed variant IDs map (O(n))
  const subscriptionAllowedVariantIds = new Map<string, Set<string>>();
  const subscriptionNodes = new Map<string, CartLineNode>();

  edges.forEach(({ node }) => {
    if (node.merchandise.product?.handle !== 'subscription-1') {
      return;
    }

    const getProperty = nodeGetPropertyMap.get(node.id);
    if (!getProperty) return;

    const subscriptionsPayload = getProperty('subscriptionsPayload');
    if (!subscriptionsPayload) return;

    // Extract all variant IDs from the add_ons array
    const variantIds = extractVariantIdsFromAddOns(subscriptionsPayload);

    if (variantIds.length > 0) {
      subscriptionAllowedVariantIds.set(node.id, new Set(variantIds));
      subscriptionNodes.set(node.id, node);
    }
  });

  // Early return if no valid subscriptions
  if (subscriptionAllowedVariantIds.size === 0) {
    return [];
  }

  // Step 3: Find mismatched book addons (O(n × m) where m = number of subscriptions, but typically m is 1-2)
  const mismatchedAddons: string[] = [];
  const processedAddons = new Set<string>(); // Prevent duplicate processing

  edges.forEach(({ node: potentialAddon }) => {
    // Skip if already processed
    if (processedAddons.has(potentialAddon.id)) {
      return;
    }

    const getProperty = nodeGetPropertyMap.get(potentialAddon.id);
    if (!getProperty) return;

    // Check if it's a book bundle
    const isBookBundle = getProperty('is_book_bundle') === 'true';
    if (!isBookBundle) return;

    // Extract the variant ID from the merchandise ID
    const variantId = extractIdFromShopifyStr(potentialAddon.merchandise.id);
    if (!variantId) return;

    // Check against each subscription
    subscriptionNodes.forEach((subscriptionNode, subscriptionId) => {
      if (isSubscriptionAddon(potentialAddon, subscriptionNode)) {
        const allowedVariantIds =
          subscriptionAllowedVariantIds.get(subscriptionId);

        if (allowedVariantIds && !allowedVariantIds.has(variantId)) {
          // Mismatch! The book bundle's variant ID is not in the subscription's allowed list
          mismatchedAddons.push(potentialAddon.id);
          processedAddons.add(potentialAddon.id);
        }
      }
    });
  });

  return mismatchedAddons;
}

/**
 * Handles incompatible items in the cart, including subscription-1 duplicates, Reading Skill Set conflicts,
 * duplicate subscription addons, and mismatched book addons.
 * @param edges - The cart line edges from the cart response.
 * @returns Promise that resolves to the updated edges if any items were removed, or the original edges if no removal occurred.
 */
export async function handleIncompatibleItems(
  edges: { node: CartLineNode }[],
): Promise<{ node: CartLineNode }[]> {
  // Get line IDs to remove for subscriptions
  const subscriptionItemsToRemove = getSubscriptionItemsToRemove(edges);

  // Get line IDs to remove for Reading Skill Set conflicts
  const readingSkillSetItemsToRemove = getReadingSkillSetItemsToRemove(edges);

  // Get line IDs to remove for duplicate subscription addons
  const duplicateSubscriptionAddonsToRemove =
    getDuplicateSubscriptionAddonsToRemove(edges);

  // Get line IDs to remove for mismatched book addons
  const mismatchedBookAddonsToRemove = getMismatchedBookAddonsToRemove(edges);

  // Combine all items to remove and deduplicate
  const allItemsToRemove = Array.from(
    new Set([
      ...subscriptionItemsToRemove,
      ...readingSkillSetItemsToRemove,
      ...duplicateSubscriptionAddonsToRemove,
      ...mismatchedBookAddonsToRemove,
    ]),
  );

  // Remove items from cart if there are any to remove
  if (allItemsToRemove.length > 0) {
    try {
      const removeResult = await removeFromCart(allItemsToRemove);
      if (removeResult.ok && 'data' in removeResult) {
        // Return the fresh edges from the server
        return removeResult.data.lines.edges;
      } else {
        console.error('Failed to remove incompatible items from cart');
        // Fallback to client-side filtering if server call fails
        const removedIdsSet = new Set(allItemsToRemove);
        return edges.filter(({ node }) => !removedIdsSet.has(node.id));
      }
    } catch (error) {
      console.error('Error removing incompatible items from cart:', error);
      // Fallback to client-side filtering if server call fails
      const removedIdsSet = new Set(allItemsToRemove);
      return edges.filter(({ node }) => !removedIdsSet.has(node.id));
    }
  }

  // No items to remove, return original edges
  return edges;
}

/**
 * Finds subscription-1 items along with their associated addons that should be removed
 * @param subscriptionLineId - The line ID of the subscription-1 item being removed
 * @param edges - All cart line edges
 * @returns Array of line IDs to remove (subscription + its addons)
 */
export function findSubscriptionItemsToRemove(
  subscriptionLineId: string,
  edges: { node: CartLineNode }[],
): string[] {
  // Find the subscription node being removed
  const subscriptionNode = edges.find(
    ({ node }) => node.id === subscriptionLineId,
  )?.node;

  if (
    !subscriptionNode ||
    subscriptionNode.merchandise.product?.handle !== 'subscription-1'
  ) {
    return []; // Not a subscription-1 item, nothing to remove
  }

  const itemsToRemove = new Set<string>();
  itemsToRemove.add(subscriptionLineId); // Add the subscription itself

  // Find all addons linked to this subscription using the shared helper
  edges.forEach(({ node: potentialAddon }) => {
    if (isSubscriptionAddon(potentialAddon, subscriptionNode)) {
      itemsToRemove.add(potentialAddon.id);
    }
  });

  return Array.from(itemsToRemove);
}

/**
 * Removes a subscription-1 item and all its associated addons
 * @param subscriptionLineId - The line ID of the subscription-1 item to remove
 * @param edges - All cart line edges
 * @returns Promise that resolves to true if the removal was successful, false otherwise
 */
export async function removeSubscriptionItems(
  subscriptionLineId: string,
  edges: { node: CartLineNode }[],
): Promise<boolean> {
  // Find all items to remove (subscription + addons)
  const lineIdsToRemove = findSubscriptionItemsToRemove(
    subscriptionLineId,
    edges,
  );

  // If only the subscription itself is being removed, let the normal flow handle it
  if (lineIdsToRemove.length <= 1) {
    return true;
  }

  // Remove subscription and all its addons
  try {
    const removeResult = await removeFromCart(lineIdsToRemove);
    return removeResult.ok;
  } catch (error) {
    console.error('Error removing subscription items from cart:', error);
    return false;
  }
}
